import { z } from "zod";

//#region src/utils/slug-validation.ts
/**
* Maximum reasonable length for a slug.
*/
const MAX_SLUG_LENGTH = 100;
/**
* Maximum reasonable length for a numeric ID.
*/
const MAX_ID_LENGTH = 20;
/**
* Helper to check if a string is a numeric ID.
*/
function isNumericId(value) {
	return /^\d+$/.test(value);
}
/**
* Valid slug pattern: alphanumeric, hyphens, underscores, and dots.
* Must start with alphanumeric character.
*/
const VALID_SLUG_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;
/**
* Validates a slug to prevent path traversal and injection attacks.
* Designed to be used with Zod's superRefine() method.
*
* @example
* ```typescript
* const OrganizationSlug = z.string()
*   .trim()
*   .superRefine(validateSlug)
*   .describe("Organization slug");
*
* const TeamSlug = z.string()
*   .trim()
*   .superRefine(validateSlug)
*   .describe("Team slug");
* ```
*/
function validateSlug(val, ctx) {
	if (val.length === 0) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Slug cannot be empty"
		});
		return;
	}
	if (val.length > MAX_SLUG_LENGTH) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: `Slug exceeds maximum length of ${MAX_SLUG_LENGTH} characters`
		});
		return;
	}
	if (!VALID_SLUG_PATTERN.test(val)) ctx.addIssue({
		code: z.ZodIssueCode.custom,
		message: "Slug must contain only alphanumeric characters, hyphens, underscores, and dots, and must start with an alphanumeric character"
	});
}
/**
* Validates a Sentry resource identifier (event, attachment, replay, profile,
* client key, dashboard) to prevent path traversal.
*
* Uses the same character set as slugs, which accommodates every identifier
* format the API returns: numeric IDs, 32-character hex IDs, hyphenated UUIDs,
* `PROJECT-1Z43` short IDs, and the literal `latest`. Rejecting `/`, `\` and `%`
* outright means a traversal or double-encoded payload never reaches the API
* client, and requiring a leading alphanumeric blocks a bare `..`.
*
* The API client percent-encodes path segments regardless (see
* `api-client/api-path.ts`); this layer exists so callers get an actionable
* validation error instead of a confusing upstream 404.
*
* @example
* ```typescript
* const EventId = z.string()
*   .trim()
*   .superRefine(validateResourceId)
*   .describe("The ID of the event");
* ```
*/
function validateResourceId(val, ctx) {
	if (val.length === 0) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "ID cannot be empty"
		});
		return;
	}
	if (val.length > MAX_SLUG_LENGTH) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: `ID exceeds maximum length of ${MAX_SLUG_LENGTH} characters`
		});
		return;
	}
	if (!VALID_SLUG_PATTERN.test(val)) ctx.addIssue({
		code: z.ZodIssueCode.custom,
		message: "ID must contain only alphanumeric characters, hyphens, underscores, and dots, and must start with an alphanumeric character"
	});
}
/**
* Validates a parameter that can be either a slug or numeric ID.
* Designed to be used with Zod's superRefine() method.
*
* @example
* ```typescript
* const ProjectSlugOrId = z.string()
*   .trim()
*   .superRefine(validateSlugOrId)
*   .describe("Project slug or numeric ID");
*
* const IssueSlugOrId = z.string()
*   .trim()
*   .superRefine(validateSlugOrId)
*   .describe("Issue slug or numeric ID");
* ```
*/
function validateSlugOrId(val, ctx) {
	if (isNumericId(val)) {
		if (val.length > MAX_ID_LENGTH) ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: `Numeric ID exceeds maximum length of ${MAX_ID_LENGTH} characters`
		});
		return;
	}
	validateSlug(val, ctx);
}

//#endregion
export { isNumericId, validateResourceId, validateSlug, validateSlugOrId };
//# sourceMappingURL=slug-validation-MJ3jU0Hd.js.map